import { lastRunPeriod } from "./enums";
import dayjs from "dayjs";
/**
 * Utility helpers to pick the earlier or later date from two optional values.
 *
 * - Accepts `Date`, `null`, or `undefined`.
 * - If both dates are provided, compares them and returns:
 *   - `minDate`: the earlier date
 *   - `maxDate`: the later date
 * - If only one value is provided, returns that value.
 * - If neither is provided, returns `null`/`undefined` (whichever applies).
 *
 * Notes:
 * - Comparison uses JavaScript `Date` value semantics (`a <= b` / `a >= b`), which compares by time.
 * - Intended for UI constraints like DatePicker `min`/`max`.
 */

type isDateOrNull = Date | null | undefined;

export const minDate = (a: isDateOrNull, b: isDateOrNull): isDateOrNull =>
    a && b ? (a <= b ? a : b) : (a ?? b);

export const maxDate = (a: isDateOrNull, b: isDateOrNull): isDateOrNull =>
    a && b ? (a >= b ? a : b) : (a ?? b);


export function getLastRunFromDate(period: number) {
    switch (period) {
        case lastRunPeriod.lastYear:
            return dayjs().subtract(1, "year").toISOString();
        case lastRunPeriod.last3Months:
            return dayjs().subtract(3, "month").toISOString();
        default:
            return "";
    }
}

// Formats a duration (in seconds) as "Nd HH:mm:ss" including the full day count.
// Days come from asDays(); we must NOT use the "D" format token, because dayjs splits a duration into 30.42-day months first and rolls the leftover into days/hours.
export const formatDurationWithDays = (seconds: number): string => {
    const d = dayjs.duration(seconds, "seconds");
    const days = Math.floor(d.asDays());
    return `${days} d ${d.subtract(days, "days").format("HH:mm:ss")}`;
};